<template>
  <form
    ref="form"
    class="search-bar"
    :class="{
      [$class('active')]: isActive,
      [$class('collapsed')]: isSearchTemplate,
    }"
    :action="$variable('routes.search')"
    method="GET"
  >
    <label
      class="visually-hidden"
      :for="searchId"
      v-text="$string('general.site_header.search_placeholder')"
    />

    <input
      :id="searchId"
      ref="input"
      v-model="searchQuery"
      class="search-bar__input text-mobile-paragraph-m-medium text-desktop-paragraph-s-medium"
      name="q"
      :placeholder="$string('general.site_header.search_placeholder')"
      type="search"
      autocomplete="off"
      @click="$event.target.select()"
    />

    <button class="search-bar__submit" type="submit">
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.search')"
      />

      <icon-search-desktop />
    </button>

    <button
      class="search-bar__clear"
      :class="[{ [$class('active')]: query.length > 0 && !isSearchTemplate }]"
      type="button"
      @click="updateQuery('')"
    >
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.clear_search')"
      />

      <icon-close />
    </button>
  </form>
</template>

<script>
/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Search bar used in the header.
 *
 * @param {Boolean} isSearchTemplate - Whether the search bar is used
 * @param {String} searchId - The search input ID, used for accessibility and to link the predictive search focus.
 * - in the search page template.
 */
import { mapActions, mapState } from 'pinia';
import usePredictiveSearchStore from '~stores/predictive-search';
import useOverlaysStore from '~stores/overlays';

import IconClose from '~icons/directional-navigation/close.svg';
import IconSearchDesktop from '~icons/general/search-desktop.svg';

export default {
  name: 'SearchBar',

  components: {
    IconClose,
    IconSearchDesktop,
  },

  props: {
    isSearchTemplate: {
      type: Boolean,
      default: false,
    },
    searchId: {
      type: String,
      default: 'header-search',
    },
  },

  computed: {
    /**
     * Map Pinia getters.
     */
    ...mapState(useOverlaysStore, ['getActiveOverlay']),

    /**
     * Map Pinia state.
     */
    ...mapState(usePredictiveSearchStore, ['query']),

    /**
     * Compute whether the search bar is active.
     * @returns {Boolean}
     */
    isActive() {
      return (
        this.getActiveOverlay?.component === 'predictive-search' &&
        !this.isSearchTemplate
      );
    },

    /**
     * Compute search query getter and setter.
     */
    searchQuery: {
      get() {
        return this.query;
      },
      set(value) {
        this.updateQuery(value);
      },
    },
  },

  watch: {
    /**
     * Watch search bar is active to update variables.
     */
    isActive() {
      this.handleStyleVariables();
    },
  },

  mounted() {
    cnvs.EventBus.listen('predictive-search:view-all', () => {
      this.$refs.form.submit();
    });

    cnvs.EventBus.listen('cnvs:resize', () => {
      this.handleStyleVariables();
    });

    cnvs.EventBus.listen('search:open', () => {
      cnvs.Focus(this.$refs.input);
    });

    if (cnvs.page.type === 'search') {
      const query = new URLSearchParams(window.location.search).get('q');
      this.updateQuery(query);
    }
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(usePredictiveSearchStore, ['updateQuery']),

    /**
     * Handle CSS variables to make the dropdown appear right under the search.
     */
    async handleStyleVariables() {
      if (!this.isActive) {
        return;
      }

      await this.$nextTick();

      const width = this.$refs.form.getBoundingClientRect().width;
      const offset = window.innerWidth - width - this.$refs.form.offsetLeft;

      const currentWidth = getComputedStyle(document.documentElement)
        .getPropertyValue('--search-bar-width')
        .replace('px', '');

      const currentOffset = getComputedStyle(document.documentElement)
        .getPropertyValue('--search-bar-offset')
        .replace('px', '');

      if (Number(currentWidth) !== width && width !== 0) {
        document.documentElement.style.setProperty(
          '--search-bar-width',
          `${width}px`,
        );
      }

      if (Number(currentOffset) !== offset && offset <= 0) {
        document.documentElement.style.setProperty(
          '--search-bar-offset',
          `${offset}px`,
        );
      }
    },
  },
};
</script>

<style lang="scss">
@import './search-bar';
</style>
